import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import toast from 'react-hot-toast';
import emailjs from '@emailjs/browser';

const FeedbackModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    type: 'suggestion', // suggestion, bug, feature
    subject: '',
    message: '',
    email: ''
  });
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.subject.trim() || !formData.message.trim()) {
      toast.error('Konu ve mesaj alanları gerekli');
      return;
    }

    setLoading(true);

    // EmailJS ile direkt e-posta gönderimi
    try {
      const templateParams = {
        to_email: 'gokturkgpt@gmail.com',
        from_email: formData.email || 'Belirtilmemiş',
        subject: `GöktürkGPT Geri Bildirim - ${formData.type.toUpperCase()}`,
        message: formData.message,
        feedback_type: formData.type,
        user_email: formData.email || 'Belirtilmemiş',
        timestamp: new Date().toLocaleString('tr-TR')
      };

      // EmailJS servisini kullan (ücretsiz)
      await emailjs.send(
        'service_g7zriza', // EmailJS Service ID
        'template_jc0mcs8', // EmailJS Template ID
        templateParams,
        'TeXS-sZUBxIKwcFN_y' // EmailJS Public Key
      );

      toast.success('Geri bildiriminiz e-posta ile gönderildi!');
      setFormData({ type: 'suggestion', subject: '', message: '', email: '' });
      onClose();
    } catch (emailError) {
      console.log('EmailJS hatası:', emailError);
      
      // Fallback: Backend API'ye gönder
      try {
        const response = await axios.post('/api/feedback', formData);
        toast.success(response.data.message);
        setFormData({ type: 'suggestion', subject: '', message: '', email: '' });
        onClose();
      } catch (apiError) {
        console.error('API hatası:', apiError);
        toast.error('Geri bildirim gönderilemedi. Lütfen tekrar deneyin.');
      }
    }

    setLoading(false);
    
    try {
      const backendUrl = process.env.REACT_APP_API_URL || 'https://ai-chat-backend.gokturkgpt.workers.dev';
      await axios.post(`${backendUrl}/api/feedback`, formData);
      toast.success('🏛️ Geri bildiriminiz gönderildi! Teşekkürler.');
      setFormData({
        type: 'suggestion',
        subject: '',
        message: '',
        email: ''
      });
      onClose();
    } catch (error) {
      toast.error('Geri bildirim gönderilemedi. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto border border-amber-200"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-2">
                <span className="text-2xl">🏛️</span>
                <h2 className="text-2xl font-bold text-amber-900">Geri Bildirim</h2>
              </div>
              <button
                onClick={onClose}
                className="text-amber-600 hover:text-amber-800 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-amber-800 mb-2">
                  📝 Bildirim Türü
                </label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-amber-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white text-amber-900"
                >
                  <option value="suggestion">💡 Öneri</option>
                  <option value="bug">🐛 Hata Bildirimi</option>
                  <option value="feature">✨ Özellik İsteği</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-amber-800 mb-2">
                  📌 Konu *
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-amber-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white text-amber-900 placeholder-amber-400"
                  placeholder="Kısa bir başlık yazın"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-amber-800 mb-2">
                  💬 Mesaj *
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-3 py-2 border border-amber-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white text-amber-900 placeholder-amber-400"
                  placeholder="Detaylı açıklama yazın..."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-amber-800 mb-2">
                  📧 Email (İsteğe bağlı)
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-amber-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white text-amber-900 placeholder-amber-400"
                  placeholder="Yanıt almak için email adresinizi girin"
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-2 text-amber-700 bg-amber-100 rounded-lg hover:bg-amber-200 transition-colors font-medium"
                >
                  ❌ İptal
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-lg hover:from-amber-700 hover:to-orange-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-lg"
                >
                  {loading ? '📤 Gönderiliyor...' : '🚀 Gönder'}
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default FeedbackModal;
